import PropTypes from 'prop-types';
import React from 'react';

import FormatState from 'notebook/mediocre/formatted-text/FormatState';
import FormattersConstants, {FormatStateMap} from 'notebook/mediocre/formatted-text/FormattersConstants';
import StaticFormatToolbar, {
  ToolbarFormatOptionWithDropdown,
  ToolbarInsertOption,
} from 'notebook/mediocre/toolbars/StaticFormatToolbar';
import InsertTableComponent from 'notebook/plain-tables/InsertTableComponent';
import InsertWellPlateMenu from 'notebook/plain-tables/InsertWellPlateMenu';
import {
  AGGREGATE_TIME,
  EditorKey,
  StepLevel,
  STEP_TYPES,
  INSERT_SYMBOL,
  INSERT_WELL_PLATE,
  TYPE_CRITICAL,
  TYPE_PAUSE,
} from 'notebook/protocols/StepConstants';
import ColorPicker from 'ui-library/ColorPicker';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ColorPickerIcon from 'ui-library/icons/ColorPickerIcon';
import MenuContentWrapper from 'ui-library/menus/MenuContentWrapper';
import OptionContainer from 'ui-library/menus/OptionContainer';
import {isFunction} from 'util/Types';

import ProtocolStoreTransforms from '../ProtocolStoreTransforms';

const {
  BOLD,
  LINK,
  ITALIC,
  UNDERLINE,
  STRIKETHROUGH,
  SUBSCRIPT,
  SUPERSCRIPT,
  FOREGROUND_COLOR,
  BACKGROUND_COLOR,
  MONOSPACE,
} = FormattersConstants;

type IsOrEvaluatesTo<TResult, TProps = ProtocolToolbarProps> = TResult | ((props: TProps) => TResult);

interface BaseProtocolToolbarOption {
  key?: string;
  format?: string;
  icon?: IsOrEvaluatesTo<BenchlingIconName | JSX.Element>;
  text?: IsOrEvaluatesTo<string>;
  active?: IsOrEvaluatesTo<boolean>;
  enabled?: IsOrEvaluatesTo<boolean>;
  visible?: IsOrEvaluatesTo<boolean>;
  submenu?: IsOrEvaluatesTo<JSX.Element>;
}

export interface ProtocolToolbarOption extends BaseProtocolToolbarOption {
  moreKey?: string;
  tooltip?: Array<string>;
}

interface ProtocolToolbarInsertOption extends BaseProtocolToolbarOption {
  key: string;
  icon: BenchlingIconName;
  text: string;
}

export interface ProtocolToolbarProps {
  locked: boolean;
  editorKey?: string;
  stepLevel?: StepLevel;
  stepType?: (typeof STEP_TYPES)[number] | null;
  stepHasTime: boolean;
  formatState: FormatStateMap;
  moreFormats?: Array<ProtocolToolbarOption>;
  hasNonEmptyTextSelection: boolean;
  formatHandler: (format: keyof FormatStateMap, data?: {color: string}) => void;
  insertHandler: (
    type: typeof INSERT_SYMBOL | EditorKey
  ) => void | ReturnType<
    | typeof ProtocolStoreTransforms.makeTable
    | typeof ProtocolStoreTransforms.makeImage
    | typeof ProtocolStoreTransforms.makeStep
    | typeof ProtocolStoreTransforms.makeTimer
  >;
  insertTableWithDimensions: (numRows: number, numCols: number) => void;
  insertWellPlateWithDimensions: (numRows: number, numCols: number) => void;
  insertMouseOverHandler: () => void;
  insertMouseOutHandler: () => void;
  mouseOverHandler: (format: string) => void;
  mouseOutHandler: () => void;
  printOptions: Array<string>;
  printHandler: (format: keyof FormatStateMap) => void;
}

function and_(...fns: Array<Function>) {
  return (props: ProtocolToolbarProps) => fns.every((fn) => fn(props));
}

function isNotLocked(props: ProtocolToolbarProps): boolean {
  return !props.locked;
}

function inText(props: ProtocolToolbarProps): boolean {
  switch (props.editorKey) {
    case EditorKey.STEPS:
      return (
        props.stepLevel != null &&
        ![StepLevel.IMAGE, StepLevel.TABLE, StepLevel.TIMER].includes(props.stepLevel)
      );
    case EditorKey.MATERIALS:
    case EditorKey.INTRO:
      return true;
    default:
      return false;
  }
}

function inStepEditor(props: ProtocolToolbarProps): boolean {
  return props.editorKey === EditorKey.STEPS;
}

function isHiddenForLocked(props: ProtocolToolbarProps): boolean {
  return props.locked;
}

function formatMap(option: ProtocolToolbarOption): ProtocolToolbarOption {
  if (option.enabled == null) {
    option = {...option, enabled: and_(isNotLocked, inText)};
  }
  return option;
}

function checkFormat(
  format: keyof FormatStateMap,
  props: ProtocolToolbarProps
): FormatStateMap[typeof format] {
  return props.formatState[format];
}

function getRequestChangeForColor(
  format: keyof FormatStateMap,
  props: ProtocolToolbarProps
): (color: string) => void {
  return (color: string) => {
    props.formatHandler(format, {color});
  };
}

const FORMAT_TEXT_GROUP = (
  [
    {format: BOLD, icon: 'format-bold', active: checkFormat.bind(null, BOLD)},
    {format: ITALIC, icon: 'format-italic', active: checkFormat.bind(null, ITALIC)},
    {format: UNDERLINE, icon: 'format-underline', active: checkFormat.bind(null, UNDERLINE)},
    {format: STRIKETHROUGH, icon: 'format-strikethrough', active: checkFormat.bind(null, STRIKETHROUGH)},
    {format: LINK, icon: 'format-link'},
    {
      format: MONOSPACE,
      icon: 'note-code',
      active: checkFormat.bind(null, MONOSPACE),
      enabled: and_(isNotLocked, inText, (props: ProtocolToolbarProps) => props.hasNonEmptyTextSelection),
    },
  ] satisfies Array<ProtocolToolbarOption>
).map((option) => formatMap(option));

const COLOR_GROUP = (
  [
    {
      format: 'color',
      icon(props: ProtocolToolbarProps) {
        const isEnabled = and_(isNotLocked, inText)(props);
        return (
          <ColorPickerIcon
            foregroundColor={isEnabled ? (checkFormat(FOREGROUND_COLOR, props) as string) : null}
            backgroundColor={isEnabled ? (checkFormat(BACKGROUND_COLOR, props) as string) : null}
          />
        );
      },
      submenu(props: ProtocolToolbarProps) {
        return (
          <ColorPicker
            foregroundColorLink={{
              value: checkFormat(FOREGROUND_COLOR, props) as string,
              requestChange: getRequestChangeForColor(FOREGROUND_COLOR, props),
            }}
            backgroundColorLink={{
              value: checkFormat(BACKGROUND_COLOR, props) as string,
              requestChange: getRequestChangeForColor(BACKGROUND_COLOR, props),
            }}
          />
        );
      },
    },
  ] satisfies Array<ProtocolToolbarOption>
).map((option) => formatMap(option));

const ADDL_FORMAT_TEXT_GROUP = (
  [
    {format: SUBSCRIPT, icon: 'format-subscript', active: checkFormat.bind(null, SUBSCRIPT)},
    {format: SUPERSCRIPT, icon: 'format-superscript', active: checkFormat.bind(null, SUPERSCRIPT)},
  ] satisfies Array<ProtocolToolbarOption>
).map((option) => formatMap(option));

const STEP_LEVEL_GROUP = (
  [
    {format: StepLevel.HEADER, icon: 'protocol-header'},
    {format: StepLevel.STEP, icon: 'protocol-step'},
    {format: StepLevel.DESC, icon: 'protocol-desc'},
  ] as Array<ProtocolToolbarOption>
).map((option) => {
  option.active = (props: ProtocolToolbarProps) => option.format === props.stepLevel;
  option.enabled = and_(isNotLocked, inText);
  option.visible = inStepEditor;
  return option;
});

const STEP_TYPE_GROUP = (
  [
    {format: TYPE_CRITICAL, icon: 'protocol-critical-step', moreKey: 'basic-step'},
    {format: TYPE_PAUSE, icon: 'protocol-pause-point', moreKey: 'basic-step'},
  ] as Array<ProtocolToolbarOption>
).map((option) => {
  option.active = (props: ProtocolToolbarProps) => option.format === props.stepType;
  option.enabled = and_(isNotLocked, inText);
  option.visible = inStepEditor;
  return option;
});

const GROUPS: Array<Array<ProtocolToolbarOption>> = [
  FORMAT_TEXT_GROUP,
  COLOR_GROUP,
  ADDL_FORMAT_TEXT_GROUP,
  STEP_LEVEL_GROUP,
  STEP_TYPE_GROUP,
  [
    {
      format: AGGREGATE_TIME,
      icon: 'protocol-aggregate-time',
      moreKey: 'basic-step',
      active(props: ProtocolToolbarProps) {
        return props.stepHasTime;
      },
      enabled: and_(
        isNotLocked,
        (props: ProtocolToolbarProps) =>
          props.stepLevel != null && [StepLevel.HEADER, StepLevel.STEP].includes(props.stepLevel)
      ),
      visible: inStepEditor,
    },
  ],
  [
    {
      format: 'delete',
      icon: 'format-delete',
      moreKey: 'basic-step',
      enabled: isNotLocked,
      visible: inStepEditor,
    },
  ],
];

const INSERTS = (
  [
    {key: StepLevel.HEADER, icon: 'protocol-header', text: 'Header'},
    {key: StepLevel.STEP, icon: 'protocol-step', text: 'Step'},
    {key: StepLevel.DESC, icon: 'protocol-desc', text: 'Description'},
    {key: StepLevel.IMAGE, icon: 'protocol-image', text: 'Image'},
    {
      key: StepLevel.TABLE,
      icon: 'protocol-table',
      text: 'Table',
      submenu(props: ProtocolToolbarProps) {
        return (
          <OptionContainer key="insert-table" isHovered={false} disableEventPropagation={true}>
            <InsertTableComponent insertTableWithDimensions={props.insertTableWithDimensions} />
          </OptionContainer>
        );
      },
    },
    {
      key: INSERT_WELL_PLATE,
      icon: 'well-plate',
      text: 'Well Plate',
      visible: inStepEditor,
      submenu({insertWellPlateWithDimensions}: ProtocolToolbarProps) {
        return (
          <MenuContentWrapper key="well-plate">
            <InsertWellPlateMenu insertWellPlateWithDimensions={insertWellPlateWithDimensions} />
          </MenuContentWrapper>
        );
      },
    },
    {key: StepLevel.TIMER, icon: 'protocol-timer', text: 'Timer'},
    {
      key: INSERT_SYMBOL,
      icon: 'note-symbol',
      text: 'Symbol',
      enabled: isNotLocked,
      visible: inText,
    },
  ] as Array<ProtocolToolbarInsertOption>
).map((option) => {
  if (option.visible == null) {
    option.visible = inStepEditor;
  }
  return option;
});

export default class ProtocolToolbar extends React.PureComponent<ProtocolToolbarProps> {
  static propTypes = {
    locked: PropTypes.bool.isRequired,
    editorKey: PropTypes.string,
    stepLevel: PropTypes.oneOf(Object.values(StepLevel)),
    stepType: PropTypes.oneOf(STEP_TYPES),
    stepHasTime: PropTypes.bool.isRequired,
    formatState: FormatState.propType.isRequired,

    formatHandler: PropTypes.func.isRequired,
    insertHandler: PropTypes.func.isRequired,
    insertTableWithDimensions: PropTypes.func.isRequired,
    insertWellPlateWithDimensions: PropTypes.func.isRequired,
    mouseOverHandler: PropTypes.func.isRequired,
    mouseOutHandler: PropTypes.func.isRequired,

    moreFormats: PropTypes.array.isRequired,
    hasNonEmptyTextSelection: PropTypes.bool.isRequired,
  };

  resolveProp = <T,>(prop: T | ((props: ProtocolToolbarProps) => T), defaultReturn?: T): T =>
    prop != null && isFunction(prop) ? prop(this.props) : (defaultReturn ?? prop);

  getGroups(): Array<Array<ToolbarFormatOptionWithDropdown>> {
    const groups = isHiddenForLocked(this.props) ? [] : GROUPS;
    const moreFormats = this.props.moreFormats ?? [];
    return groups.concat([moreFormats]).map((group) =>
      group.map(
        (option) =>
          ({
            active: this.resolveProp(option.active, false),
            enabled: this.resolveProp(option.enabled ?? true),
            icon: typeof option.icon === 'string' ? option.icon : this.resolveProp(option.icon, undefined),
            format: option.format,
            moreKey: option.moreKey,
            submenu: this.resolveProp(option.submenu, undefined),
            text: this.resolveProp(option.text),
            tooltip: option.tooltip,
            visible: this.resolveProp<boolean>(option.visible ?? true),
          }) satisfies ToolbarFormatOptionWithDropdown
      )
    );
  }

  getInserts(): Array<ToolbarInsertOption> {
    return INSERTS.map(
      (option) =>
        ({
          key: option.key,
          icon: option.icon,
          text: this.resolveProp(option.text),
          enabled: this.resolveProp(option.enabled ?? true),
          visible: this.resolveProp(option.visible ?? true),
          submenu: this.resolveProp(option.submenu, undefined),
        }) satisfies ToolbarInsertOption
    );
  }

  getMappedPrintOptions(printOptions: Array<string>): Array<{type: string; enabled: true}> {
    return printOptions.map((printOption) => ({type: printOption, enabled: true}));
  }

  render(): JSX.Element {
    return (
      <StaticFormatToolbar
        groups={this.getGroups()}
        formatHandler={this.props.formatHandler}
        insertHandler={this.props.insertHandler}
        insertMouseOverHandler={this.props.insertMouseOverHandler}
        insertMouseOutHandler={this.props.insertMouseOutHandler}
        mouseOverHandler={this.props.mouseOverHandler}
        mouseOutHandler={this.props.mouseOutHandler}
        printOptions={this.getMappedPrintOptions(this.props.printOptions)}
        printHandler={this.props.printHandler}
        inserts={this.getInserts()}
      />
    );
  }
}
